import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useCreateSubject, useUpdateSubject } from '../hooks/useSubjects';
import { SubjectFormData, DomainRouteParams } from '../types';
import { SubjectTableData } from '../types';

interface AddSubjectDialogProps {
  open: boolean;
  onClose: () => void;
  editingItem?: SubjectTableData | null;
  onSuccess?: () => void;
}

export const AddSubjectDialog: React.FC<AddSubjectDialogProps> = ({
  open,
  onClose,
  editingItem,
  onSuccess,
}) => {
  const { tenantId, domainId } = useParams<DomainRouteParams>();
  const isEditing = !!editingItem;
  const createSubjectMutation = useCreateSubject(tenantId!, domainId!);
  const updateSubjectMutation = useUpdateSubject(tenantId!, domainId!);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<SubjectFormData>({
    mode: 'onChange',
    defaultValues: {
      name: editingItem?.name || '',
      description: editingItem?.description || '',
      code: editingItem?.code || '',
      enrollmentCount: editingItem?.enrollmentCount || undefined,
    },
  });

  // Reset form when dialog opens/closes or editing item changes
  React.useEffect(() => {
    if (open) {
      reset({
        name: editingItem?.name || '',
        description: editingItem?.description || '',
        code: editingItem?.code || '',
        enrollmentCount: editingItem?.enrollmentCount || undefined,
      });
    }
  }, [open, editingItem, reset]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data: SubjectFormData) => {
    try {
      if (isEditing && editingItem) {
        await updateSubjectMutation.mutateAsync({
          id: editingItem.id,
          data: {
            name: data.name,
            description: data.description || undefined,
            code: data.code,
            enrollmentCount: data.enrollmentCount || undefined,
          },
        });
      } else {
        await createSubjectMutation.mutateAsync({
          name: data.name,
          description: data.description || undefined,
          code: data.code,
          enrollmentCount: data.enrollmentCount || undefined,
        });
      }

      // Close dialog and reset form on success
      handleClose();
      onSuccess?.();
    } catch (error) {
      // Error handling is done in the mutation hooks via notifications
      console.error('Failed to save subject:', error);
    }
  };

  const isLoading = createSubjectMutation.isPending || updateSubjectMutation.isPending;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={isLoading}
    >
      <DialogTitle>{isEditing ? 'Edit Subject' : 'Add New Subject'}</DialogTitle>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Controller
              name="name"
              control={control}
              rules={{
                required: 'Subject name is required',
                minLength: {
                  value: 2,
                  message: 'Subject name must be at least 2 characters',
                },
                maxLength: {
                  value: 100,
                  message: 'Subject name must not exceed 100 characters',
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Subject Name"
                  variant="outlined"
                  fullWidth
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  disabled={isLoading}
                  placeholder="e.g., Introduction to Computer Science"
                />
              )}
            />

            <Controller
              name="code"
              control={control}
              rules={{
                required: 'Subject code is required',
                maxLength: {
                  value: 20,
                  message: 'Subject code must not exceed 20 characters',
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Subject Code"
                  variant="outlined"
                  fullWidth
                  error={!!errors.code}
                  helperText={
                    errors.code?.message || 'Short code for the subject (max 20 characters)'
                  }
                  disabled={isLoading}
                  placeholder="e.g., CS101, INF100"
                />
              )}
            />

            <Controller
              name="description"
              control={control}
              rules={{
                maxLength: {
                  value: 500,
                  message: 'Description must not exceed 500 characters',
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Description"
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={3}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                  disabled={isLoading}
                  placeholder="Optional description for the subject..."
                />
              )}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={isLoading} color="inherit">
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={!isValid || isLoading}
            startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
          >
            {isLoading
              ? isEditing
                ? 'Updating...'
                : 'Creating...'
              : isEditing
                ? 'Update Subject'
                : 'Create Subject'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
